import { Users, User } from '../models/user';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ExportDownloadFormats } from '../domain/export/export-download.service';

const FindUserById = (users: Users, id: number): User =>
  users && users.results ? users.results.find(u => u.id === id) || null : null;

const FormatUserRealname = (user: User): string => (user ? user.realname : null);

const ExportDataCSV2 = (documentName: string, data: any) => {
  var csvFormattedDataTable = data
    .map(row =>
      row
        .map(element =>
          typeof element === 'string' && element.includes(',')
            ? `"${element}"`
            : element
        )
        .join(',')
    )
    .join('\n');
  if (csvFormattedDataTable.includes('#')) {
    csvFormattedDataTable = csvFormattedDataTable.replace('#', '-');
  }
  let csvContent = new Blob(['\ufeff', csvFormattedDataTable], {
    type: 'text/csv;charset=utf-8;'
  });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(csvContent);
  link.download = documentName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
};

const ExportDataExcel = (documentName: string, data: any[][]) => {
  // Normalize newlines so values are always visible in Excel without wrap quirks
  const normalized = (data || []).map(row =>
    (row || []).map(cell =>
      typeof cell === 'string' ? cell.replace(/\r?\n/g, ' | ') : cell
    )
  );
  const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(normalized);
  const colCount = (normalized[0] || []).length;
  worksheet['!cols'] = Array.from({ length: colCount }, () => ({ wch: 32 }));
  const workbook: XLSX.WorkBook = {
    Sheets: { data: worksheet },
    SheetNames: ['data']
  };
  const excelBuffer: any = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });
  const blob = new Blob([excelBuffer], {
    type:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  });
  const name = documentName.endsWith('.xlsx')
    ? documentName
    : `${documentName}.xlsx`;
  downloadBlob(blob, name);
};

const ExportDataPdf = (documentName: string, data: any[][]) => {
  const doc = new jsPDF('l', 'mm', 'a4');
  const headers = (data[0] || []).map(h =>
    typeof h === 'string' ? h.replace(/\r?\n/g, ' ') : h
  );
  const records = data.slice(1).map(row =>
    (row || []).map(cell =>
      typeof cell === 'string' ? cell.replace(/\r?\n/g, ' | ') : cell ?? ''
    )
  );
  const allRows: any[] = [];

  records.forEach((record, idx) => {
    const rowGroup: any[][] = [];
    for (let i = 0; i < headers.length; i += 3) {
      const cells = [0, 1, 2].map(offset => {
        const hi = i + offset;
        if (hi >= headers.length) {
          return '';
        }
        const label = headers[hi] ?? '';
        const value = record[hi] ?? '';
        const text = `${label}: ${value}`.trim();
        return text === ':' ? '' : text;
      });
      rowGroup.push(cells);
    }
    rowGroup.unshift([`Registro #${idx + 1}`, '', '']);
    allRows.push(...rowGroup, [' ', ' ', '']);
  });

  autoTable(doc, {
    startY: 10,
    body: allRows,
    theme: 'grid',
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      overflow: 'linebreak',
      valign: 'top',
      minCellHeight: 6
    },
    columnStyles: {
      0: { cellWidth: 93 },
      1: { cellWidth: 93 },
      2: { cellWidth: 93 }
    },
    head: [],
    didDrawPage: () => {
      doc.setFontSize(9);
      doc.text('Exportacion de informes', 10, 6);
    }
  });

  const name = documentName.endsWith('.pdf')
    ? documentName
    : `${documentName}.pdf`;
  doc.save(name);
};

/**
 * Export reports as Excel and/or PDF.
 * Defaults to both when formats omitted (legacy behavior).
 */
const ExportDataCSV = (
  documentName: string,
  data: any[][],
  formats?: Partial<ExportDownloadFormats>
) => {
  // Explicit flags only — never download both by accident when one is false.
  const excel = formats ? !!formats.excel : true;
  const pdf = formats ? !!formats.pdf : false;
  if (excel) {
    ExportDataExcel(documentName, data);
  }
  if (pdf) {
    ExportDataPdf(documentName, data);
  }
};

export const UsersHelpers = {
  FindUserById,
  FormatUserRealname,
  ExportDataCSV,
  ExportDataExcel,
  ExportDataPdf
};
